import React, { useCallback, useEffect, useState } from 'react';

import { faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import styled from 'styled-components';

import { FileModel, TagModel, WorklistViewModel } from 'models';

import { MOBILE_BREAKPOINT, useMobileDetection } from 'core/hooks';
import { Button, ButtonVariants, Icon } from 'core/ui';

import { useAccessTokenSnapshot } from 'features/auth';
import { FileViewerPage } from 'features/file/fragments/FileViewerPage';
import { useFilePreload } from 'features/file/hooks/useFilePreload';

import { PatientFilePreviewEdit, SubmissionModel } from './PatientFilePreviewEdit';
import { PatientFilesBreadcrumb } from './PatientFilesBreadcrumb';

// Copied Styles from PatientFiles.tsx
const ViewerHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.colors.palette.grayscale[3] ?? '#ddd'};
  background-color: ${({ theme }) => theme.colors.palette.grayscale[0]};
  position: sticky;
  top: 0;
  z-index: 10;
`;

const BackButton = styled(Button)`
  margin-right: 1rem;
  padding: 0.3rem 0.8rem;
  svg {
    margin-right: 0.5rem;
  }
`;

const ViewerTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-grow: 1;
  max-width: 50vw;
`;

const IntegratedLayout = styled.div<{ $sidebarExpanded: boolean }>`
  /* Use proper CSS Grid layout following our guidelines */
  display: grid;
  grid-template-columns: ${({ $sidebarExpanded }) => ($sidebarExpanded ? '1fr 350px' : '1fr 0px')};
  gap: ${({ $sidebarExpanded }) => ($sidebarExpanded ? '1rem' : '0')};
  min-height: 0; /* Allow shrinking below content size */
  overflow: hidden;
  transition: grid-template-columns 0.3s ease;
  position: relative;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const MainViewerArea = styled.div`
  /* Follow CSS Layout Guide: Fill Available Space Pattern */
  display: grid;
  grid-template-rows: 1fr;
  min-height: 0; /* Allow shrinking below content size */
  overflow: hidden; /* Prevent scrolling at this level */
`;

const SidebarArea = styled.div<{ $expanded: boolean }>`
  /* Use CSS Grid for sidebar layout */
  display: grid;
  grid-template-rows: min-content 1fr; /* header, content */
  min-height: 0; /* Allow shrinking */
  overflow: hidden;
  border-left: ${({ $expanded, theme }) => ($expanded ? `1px solid ${theme.colors.palette.grayscale[3] ?? '#ddd'}` : 'none')};
  background-color: #ffffff;
  transition: all 0.3s ease;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    position: ${({ $expanded }) => ($expanded ? 'fixed' : 'static')};
    top: ${({ $expanded }) => ($expanded ? '0' : 'auto')};
    left: ${({ $expanded }) => ($expanded ? '0' : 'auto')};
    right: ${({ $expanded }) => ($expanded ? '0' : 'auto')};
    bottom: ${({ $expanded }) => ($expanded ? '0' : 'auto')};
    z-index: ${({ $expanded }) => ($expanded ? '1000' : 'auto')};
    width: ${({ $expanded }) => ($expanded ? '100vw' : 'auto')};
    min-height: ${({ $expanded }) => ($expanded ? '100vh' : '0')};
    display: ${({ $expanded }) => ($expanded ? 'grid' : 'none')};
  }
`;

const SidebarHeader = styled.div`
  /* Header that sizes to content */
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #e8e8e8;
  background-color: #f8f9fa;
  min-height: 0; /* Grid item sizing */
`;

const SidebarTitle = styled.h4`
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SidebarToggleButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #6c757d;
  font-size: 1rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #343a40;
  }
`;

const SidebarContent = styled.div<{ $expanded: boolean }>`
  /* Grid item that fills remaining sidebar space with proper constraints */
  display: grid;
  grid-template-rows: 1fr; /* Fill available space */
  min-height: 0; /* Allow shrinking */
  max-height: 100%; /* Prevent overflow beyond container */
  overflow: hidden; /* Delegate scrolling to child components */
  width: ${({ $expanded }) => ($expanded ? '350px' : '0px')};
  opacity: ${({ $expanded }) => ($expanded ? '1' : '0')};
  transition:
    width 0.3s ease,
    opacity 0.3s ease;

  @media (max-width: 768px) {
    width: 100%;
    opacity: 1;
  }
`;

const SidebarExpandButton = styled.button<{ $expanded: boolean }>`
  position: fixed;
  right: ${({ $expanded }) => ($expanded ? '350px' : '1em')};
  top: 93px; /* Position higher to avoid overlapping navigation buttons */
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: solid 1px ${({ theme }) => theme.colors.palette.black ?? '#ddd'};
  border-radius: 4px;
  height: 3em;
  cursor: pointer;
  z-index: 100;
  transition: right 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);

  &:hover {
    background: #0056b3;
  }
`;

const StyledFileViewerContainer = styled.div`
  /* Use grid layout for file viewer container */
  display: grid;
  grid-template-rows: 1fr;
  min-height: 0; /* Allow shrinking within parent grid */
  overflow: hidden; /* Let FileViewerPage handle its own overflow */
`;

const StyledFileViewerPage = styled(FileViewerPage)`
  padding: 0;
  margin: 0;
`;

// Root container for the entire file viewer layout
const FileViewerRoot = styled.div`
  /* Follow CSS Layout Guide: Multi-Section Layout Pattern */
  display: grid;
  grid-template-rows: min-content min-content 1fr; /* header, breadcrumb, content */
  height: 100%; /* Fill available space in Page component */
  min-height: 0; /* Allow shrinking below content size */
  overflow: hidden; /* Prevent root scrolling, delegate to children */
`;

// Stack overlay for showing worklist/virtual folder name
const StackOverlay = styled.div`
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.85) 0%, rgba(0, 0, 0, 0.75) 100%);
  color: white;
  padding: 0.75rem 1.25rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  z-index: 100;
  max-width: 320px;
  word-wrap: break-word;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.05) 100%);
    border-radius: 10px;
    z-index: -1;
  }

  @media (max-width: 768px) {
    top: 1rem;
    left: 1rem;
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
    max-width: 280px;
  }
`;

interface FileViewerIntegratedProps {
  viewingFile: FileModel;
  viewingFileContext: FileModel[];
  onCloseViewer: () => void;
  onFileSelection?: (file: FileModel) => void;
  onFileDeleted: (fileId: string | number) => Promise<void>;
  onFileRenamed: (file: FileModel, newName: string) => Promise<void>;
  onFileDescriptionUpdated: (file: FileModel, description: string) => Promise<void>;
  onFileTimestampsUpdated: (file: FileModel, captureTime?: Date | null, eventTime?: Date | null) => Promise<void>;
  onFileTagsUpdated: (fileId: number, tagIds: number[]) => Promise<void>;
  onFileDownloaded: (file: FileModel) => Promise<void>;
  allTagsFlat: TagModel[];
  tagIdToTagMap: Map<number, TagModel>;
  allHierarchicalTags: TagModel[];
  areTagsLoading?: boolean;
  formSubmissions?: SubmissionModel[];
  areFormSubmissionsLoading?: boolean;
  onFormSubmissionsUpdated?: () => Promise<void>;
  // Breadcrumb props
  patientId: number;
  currentPath?: string;
  // Worklist props for breadcrumb and stack overlay
  selectedWorklist?: WorklistViewModel | null;
  allWorklists?: WorklistViewModel[] | null;
  stackName?: string; // For showing virtual "folder" name when viewing image stacks
}

const FileViewerIntegratedInner: React.FC<FileViewerIntegratedProps> = ({
  viewingFile,
  viewingFileContext,
  onCloseViewer,
  onFileSelection,
  onFileDeleted,
  onFileRenamed,
  onFileDescriptionUpdated,
  onFileTimestampsUpdated,
  onFileTagsUpdated,
  onFileDownloaded,
  allTagsFlat,
  tagIdToTagMap,
  allHierarchicalTags,
  areTagsLoading,
  formSubmissions,
  areFormSubmissionsLoading,
  onFormSubmissionsUpdated,
  patientId,
  currentPath = '',
  selectedWorklist,
  allWorklists,
  stackName,
}) => {
  // Use props directly since context is not always available
  const effectiveSelectedWorklist = selectedWorklist;
  const effectiveAllWorklists = allWorklists;
  const effectiveStackName = stackName;
  const effectiveCurrentPath = currentPath;

  // Mobile detection using consolidated utility
  const { isMobile } = useMobileDetection();

  // Sidebar state - start collapsed on mobile
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(() => !isMobile);

  // Local state for current viewing file to handle immediate title updates during navigation
  const [currentViewingFile, setCurrentViewingFile] = useState<FileModel>(viewingFile);

  // Update local state when props change
  useEffect(() => {
    setCurrentViewingFile(viewingFile);
  }, [viewingFile]);

  // Enhanced file selection handler that updates local state immediately
  const handleFileSelectionWithImmediateUpdate = useCallback(
    (file: FileModel) => {
      // Update local state immediately for instant title update
      setCurrentViewingFile(file);
      // Call parent handler for URL navigation and state management
      onFileSelection?.(file);
    },
    [onFileSelection],
  );

  // Auto-collapse sidebar when switching to mobile
  useEffect(() => {
    if (isMobile) {
      setIsSidebarExpanded(false);
    }
  }, [isMobile]);

  // Get access token for authenticated file requests
  const { accessToken } = useAccessTokenSnapshot();

  // Progressive file preloading using the dedicated service
  const { preloadFile, clearPreloads, getStats } = useFilePreload(
    viewingFileContext,
    viewingFile?.id,
    {
      enabled: true,
      maxConcurrentPreloads: 4, // Increased for better performance
      preloadRange: Math.max(10, Math.ceil((viewingFileContext?.length || 0) / 2)), // Preload all files or at least 10
      batchDelay: 200, // Faster batching for better responsiveness
      priorityTypes: ['image', 'pdf'], // Focus on visual files
      maxFileSize: 50 * 1024 * 1024, // Increased to 50MB limit
      debounceMs: 150, // Faster response to file changes
    },
    accessToken ?? undefined,
  );

  if (!viewingFile) {
    return null;
  }

  return (
    <FileViewerRoot>
      <ViewerHeader>
        <BackButton variant={ButtonVariants.SECONDARY} onClick={onCloseViewer} title="Back to previous view">
          <Icon icon={faChevronLeft} /> Back
        </BackButton>
        <ViewerTitle title={currentViewingFile.fileName}>{currentViewingFile.fileName}</ViewerTitle>
      </ViewerHeader>
      {!isMobile && (
        <PatientFilesBreadcrumb
          patientId={patientId}
          hierarchicalPath={effectiveCurrentPath}
          mergedFlatTags={allTagsFlat}
          mergedTagNameToIdMap={undefined}
          selectedWorklist={effectiveSelectedWorklist}
          allWorklists={effectiveAllWorklists}
          stackName={effectiveStackName}
        />
      )}
      <IntegratedLayout $sidebarExpanded={isSidebarExpanded}>
        <MainViewerArea>
          <StyledFileViewerContainer>
            {effectiveStackName && <StackOverlay>{effectiveStackName}</StackOverlay>}
            <StyledFileViewerPage
              files={viewingFileContext}
              selectedFile={viewingFile}
              onFileSelect={handleFileSelectionWithImmediateUpdate}
              showDeleteBtn={false}
              showEditBtn={false}
              showInfobar={false}
              showSidebar={false}
              showGallery={true} /* Always show thumbnail gallery */
              showNavButtons={true}
              showTimeline={false}
              examId={viewingFile.examId ?? undefined}
            />
          </StyledFileViewerContainer>
        </MainViewerArea>
        <SidebarArea $expanded={isSidebarExpanded}>
          {isSidebarExpanded && (
            <>
              <SidebarHeader>
                <SidebarTitle>{currentViewingFile.fileName ?? 'File Details'}</SidebarTitle>
                <SidebarToggleButton onClick={() => setIsSidebarExpanded(false)} title="Collapse sidebar">
                  <Icon icon={faChevronRight} />
                </SidebarToggleButton>
              </SidebarHeader>
              <SidebarContent $expanded={isSidebarExpanded}>
                <PatientFilePreviewEdit
                  selectedFile={currentViewingFile}
                  onView={() => console.log('Integrated view: onView clicked in sidebar - should this open a new modal or focus?')}
                  onDownload={onFileDownloaded}
                  onDelete={() => onFileDeleted(currentViewingFile.id)}
                  onRename={onFileRenamed}
                  onUpdateDescription={onFileDescriptionUpdated}
                  onUpdateTimestamps={onFileTimestampsUpdated}
                  allTagsFlat={allTagsFlat}
                  tagIdToTagMap={tagIdToTagMap}
                  includeThumbnail={false} // Don't show thumbnail in sidebar when main viewer is already showing the file
                  showViewButton={false} // Hide View button in gallery mode since user is already viewing the file
                  //showFileDetails={false} // Hide file details when viewing specific file in integrated mode
                  //showThumbnailGallery={false} // Hide thumbnail gallery in sidebar
                  allHierarchicalTags={allHierarchicalTags}
                  onUpdateTags={onFileTagsUpdated}
                  areTagsLoading={areTagsLoading}
                  formSubmissions={formSubmissions ?? []}
                  areFormSubmissionsLoading={areFormSubmissionsLoading ?? false}
                  onFormUpdated={
                    onFormSubmissionsUpdated ??
                    (async () => {
                      console.warn('onFormUpdated not implemented in FileViewerIntegrated');
                    })
                  }
                  selectedWorklist={effectiveSelectedWorklist}
                  allWorklists={effectiveAllWorklists}
                  // Notes functionality props
                  patientId={patientId}
                  allFiles={viewingFileContext}
                  onNoteCreated={(noteData) => {
                    console.log('Note created in file viewer:', noteData);
                    // Could trigger a refresh or update state here
                  }}
                />
              </SidebarContent>
            </>
          )}
        </SidebarArea>
        <SidebarExpandButton
          $expanded={isSidebarExpanded}
          onClick={() => setIsSidebarExpanded(true)}
          title="Expand sidebar"
          style={{ display: isSidebarExpanded ? 'none' : 'flex' }}
        >
          <Icon icon={faChevronLeft} />
        </SidebarExpandButton>
      </IntegratedLayout>
    </FileViewerRoot>
  );
};

export const FileViewerIntegrated = FileViewerIntegratedInner;
